import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, RefreshCw, FileDown, Search, AlertTriangle, Sparkles,
  Users2, LayoutGrid, Zap, UserCog, Info,
} from "lucide-react";
import { toast } from "sonner";

const SHIFT_COLOR = {
  ADMINISTRATIVO: "border-[#234935]/30 bg-[#234935]/[0.04]",
  A: "border-sky-200 bg-sky-50/50",
  B: "border-amber-200 bg-amber-50/50",
  C: "border-violet-200 bg-violet-50/50",
  D: "border-rose-200 bg-rose-50/50",
};
const SHIFT_BADGE = {
  ADMINISTRATIVO: "bg-[#234935]/10 text-[#234935] border-[#234935]/20",
  A: "bg-sky-100 text-sky-700 border-sky-200",
  B: "bg-amber-100 text-amber-700 border-amber-200",
  C: "bg-violet-100 text-violet-700 border-violet-200",
  D: "bg-rose-100 text-rose-700 border-rose-200",
};

export default function SectorAutoGridPage() {
  const { id } = useParams();
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterSub, setFilterSub] = useState("all");
  const [filterShift, setFilterShift] = useState("all");
  const [unassignedOpen, setUnassignedOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (filterSub !== "all") params.sub_setor = filterSub;
      if (filterShift !== "all") params.turno = filterShift;
      const { data } = await api.get(`/sectors/${id}/work-grid`, { params });
      setGrid(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [id, search, filterSub, filterShift]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(`${API_BASE}/sectors/${id}/work-grid/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `quadro_auto_${grid?.sector?.name || id}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Falha ao exportar"); }
  };

  if (loading || !grid) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const { sector, shifts, shift_labels, subsectors, cells, indicators, unassigned, all_subsector_options } = grid;

  return (
    <div className="space-y-5" data-testid="sector-auto-grid">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/25 bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-6 text-white">
        <div aria-hidden className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C5A059]/20 blur-[80px]" />
        <div className="relative">
          <Link to="/setores" className="inline-flex items-center gap-1.5 text-[11.5px] text-white/60 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar para Setores
          </Link>
          <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-medium flex items-center gap-1.5">
                <Zap className="h-3 w-3" /> Quadro Automático · Fonte: cadastro do colaborador
              </div>
              <h1 className="mt-1 font-display text-3xl md:text-[32px] font-semibold tracking-tight">
                {sector.name}
              </h1>
              <p className="mt-2 text-[13px] text-white/70 max-w-[720px]">
                Este quadro é montado <b>automaticamente</b> a partir dos campos <i>Sub-setor</i> e <i>Turno operacional</i> do cadastro.
                Qualquer alteração no cadastro reflete aqui em tempo real.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={load} className="border-white/20 bg-white/5 text-white hover:bg-white/10" data-testid="sag-refresh">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Atualizar
              </Button>
              <Button size="sm" variant="outline" onClick={exportExcel} className="border-white/20 bg-white/5 text-white hover:bg-white/10" data-testid="sag-export">
                <FileDown className="h-3.5 w-3.5 mr-1" /> Excel
              </Button>
              <Link to="/colaboradores">
                <Button size="sm" className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]" data-testid="sag-edit-employees">
                  <UserCog className="h-3.5 w-3.5 mr-1" /> Editar colaboradores
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="sag-kpis">
        <Kpi label="Total no setor" value={indicators.total_employees} accent />
        <Kpi label="Administ." value={indicators.total_admin} />
        <Kpi label="Turno A" value={indicators.total_shift_a} />
        <Kpi label="Turno B" value={indicators.total_shift_b} />
        <Kpi label="Turno C" value={indicators.total_shift_c} />
        <Kpi label="Turno D" value={indicators.total_shift_d} />
        <button
          onClick={() => setUnassignedOpen(true)}
          data-testid="sag-unassigned-btn"
          disabled={indicators.total_missing_data === 0}
          className={`rounded-lg border p-3 text-left transition-colors ${
            indicators.total_missing_data > 0
              ? "border-amber-300 bg-amber-50 hover:bg-amber-100 cursor-pointer"
              : "border-border bg-white opacity-60 cursor-default"
          }`}
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <AlertTriangle className="h-3 w-3" /> Sem dados
          </div>
          <div className={`font-display text-xl font-semibold mt-0.5 ${indicators.total_missing_data > 0 ? "text-amber-700" : "text-slate-700"}`}>
            {indicators.total_missing_data}
          </div>
        </button>
      </div>

      {/* How it works tip (first-time help) */}
      {subsectors.length === 0 && (
        <div className="rounded-xl border border-[#C5A059]/25 bg-[#FBF8F0] p-6" data-testid="sag-empty-hint">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-[#C5A059] shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display text-lg font-semibold">Nenhum colaborador deste setor tem dados operacionais ainda</h3>
              <p className="mt-1.5 text-[13px] text-slate-700 leading-relaxed max-w-[680px]">
                Para montar o quadro, abra a página de <b>Colaboradores</b>, edite cada pessoa deste setor e preencha a seção <b>Informações Operacionais</b> (Sub-setor + Turno). Ao salvar, o colaborador aparece automaticamente aqui.
              </p>
              <Link to="/colaboradores">
                <Button className="mt-4 bg-[#234935] hover:bg-[#2c5c43] text-white" data-testid="sag-goto-employees">
                  <UserCog className="h-3.5 w-3.5 mr-1.5" /> Ir para Colaboradores
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-lg border border-border bg-white p-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, matrícula ou cargo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="sag-search"
          />
        </div>
        <Select value={filterSub} onValueChange={setFilterSub}>
          <SelectTrigger className="w-[200px]" data-testid="sag-filter-sub"><SelectValue placeholder="Sub-setor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os sub-setores</SelectItem>
            {all_subsector_options.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterShift} onValueChange={setFilterShift}>
          <SelectTrigger className="w-[180px]" data-testid="sag-filter-shift"><SelectValue placeholder="Turno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os turnos</SelectItem>
            {shifts.map(sh => <SelectItem key={sh} value={sh}>{shift_labels[sh]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {subsectors.length > 0 && (
        <div className="rounded-xl border border-border bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]" data-testid="sag-grid-table">
              <thead>
                <tr className="bg-gradient-to-b from-slate-50 to-white border-b border-border">
                  <th className="sticky left-0 bg-slate-50 z-10 px-4 py-3.5 text-left font-semibold text-slate-700 w-[220px] min-w-[220px]">
                    Sub-setor
                  </th>
                  {shifts.map(sh => (
                    <th key={sh} className="px-3 py-3.5 text-left font-semibold text-slate-700 min-w-[200px] border-l border-border/60">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] uppercase tracking-[0.18em] font-medium ${SHIFT_BADGE[sh]}`}>
                          {shift_labels[sh]}
                        </span>
                        <Badge variant="outline" className="text-[10px] tabular-nums">{indicators.totals_by_shift[sh] || 0}</Badge>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subsectors.map((sub, idx) => (
                  <tr key={sub} className={idx % 2 ? "bg-slate-50/30" : ""}>
                    <td className="sticky left-0 bg-inherit z-10 px-4 py-3 border-b border-border/60 align-top">
                      <div className="font-medium text-slate-800">{sub}</div>
                      <div className="text-[10.5px] text-muted-foreground mt-0.5 uppercase tracking-wider flex items-center gap-1">
                        <Users2 className="h-2.5 w-2.5" />
                        {shifts.reduce((acc, sh) => acc + (cells[sub]?.[sh]?.length || 0), 0)} colab.
                      </div>
                    </td>
                    {shifts.map(sh => {
                      const emps = cells[sub]?.[sh] || [];
                      return (
                        <td
                          key={sh}
                          className={`px-2 py-2 align-top border-l border-b border-border/60 ${emps.length === 0 ? "" : SHIFT_COLOR[sh]}`}
                          data-testid={`sag-cell-${sub}-${sh}`}
                        >
                          {emps.length === 0 ? (
                            <div className="text-[11px] text-muted-foreground/60 italic px-2 py-6 text-center">—</div>
                          ) : (
                            <div className="space-y-1.5">
                              {emps.map(e => <EmployeePill key={e.id} emp={e} />)}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unassigned dialog */}
      <Dialog open={unassignedOpen} onOpenChange={setUnassignedOpen}>
        <DialogContent className="max-w-[720px]">
          <DialogHeader>
            <DialogTitle>Colaboradores sem dados operacionais</DialogTitle>
            <DialogDescription>
              Os colaboradores abaixo pertencem a este setor mas não têm <b>Sub-setor</b> e/ou <b>Turno operacional</b> cadastrados. Por isso não aparecem no quadro.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[440px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Dados faltantes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unassigned.map(e => {
                  const missing = [];
                  if (!e.sub_setor) missing.push("Sub-setor");
                  if (!e.turno_operacional) missing.push("Turno");
                  return (
                    <TableRow key={e.id}>
                      <TableCell className="text-[12.5px] font-medium">{e.nome}</TableCell>
                      <TableCell className="text-[11.5px] font-mono tabular-nums">{e.matricula || "—"}</TableCell>
                      <TableCell className="text-[12px] text-muted-foreground">{e.cargo || "—"}</TableCell>
                      <TableCell>
                        {missing.map(m => (
                          <Badge key={m} variant="outline" className="mr-1 text-[10px] border-amber-300 text-amber-700 bg-amber-50">{m}</Badge>
                        ))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="pt-2">
            <Link to="/colaboradores">
              <Button className="bg-[#234935] hover:bg-[#2c5c43] text-white w-full" data-testid="sag-goto-employees-fix">
                Ir para Colaboradores e preencher os dados
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`rounded-lg border p-3 ${accent ? "border-[#234935]/30 bg-[#234935]/5" : "border-border bg-white"}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`font-display text-xl font-semibold mt-0.5 tabular-nums ${accent ? "text-[#234935]" : "text-slate-800"}`}>{value ?? 0}</div>
    </div>
  );
}

function EmployeePill({ emp }) {
  return (
    <div
      className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] hover:border-[#C5A059]/50 hover:shadow-sm transition-all"
      data-testid={`sag-emp-${emp.id}`}
    >
      <div className="font-medium truncate text-slate-800">{emp.nome}</div>
      <div className="text-[10.5px] text-muted-foreground font-mono tabular-nums">
        {emp.matricula || "—"}
        {emp.cargo && <span className="font-sans"> · {emp.cargo}</span>}
      </div>
    </div>
  );
}
